// Copyright (c) Ping. All rights reserved.
//
// LiveKit room manager — the media engine for calls. Wraps `livekit-client`
// `Room` and exposes a small, framework-agnostic surface the call store drives.
//
// E2EE: media frames are encrypted with the MLS-derived media key (mediaKey.ts)
// via LiveKit's ExternalE2EEKeyProvider + e2ee Web Worker — the SFU forwards
// only ciphertext. E2EE needs WebRTC Encoded Transform (insertable streams on
// Chromium, RTCRtpScriptTransform on Safari 15.4+). Where it's unavailable we
// connect WITHOUT e2ee (transport DTLS-SRTP still applies) and surface
// `e2eeActive=false` so the UI can drop the lock badge. Calls always work.
//
// SCALE (30+): `adaptiveStream` (only attached+visible tiles get subscribed +
// sized) + `dynacast` (publishers drop unused layers) + simulcast. The grid
// attaches only the current page, so a 30-person call decodes a handful of
// streams, not 30.
//
// AUDIO: remote audio tracks are attached to managed <audio> sinks (LiveKit
// does NOT auto-play remote audio). Browsers may block autoplay until a user
// gesture — `startAudioPlayback()` resumes it (call from a click).

import {
  ConnectionState,
  ExternalE2EEKeyProvider,
  type LocalVideoTrack,
  type Participant,
  type RemoteAudioTrack,
  type RemoteParticipant,
  type RemoteTrack,
  type RemoteTrackPublication,
  Room,
  RoomEvent,
  Track,
  type TrackPublication,
  type VideoPreset,
  VideoPresets,
} from 'livekit-client';

import { isSafari } from '@calls/platform';

import { toKeyBytes } from './mediaKey';

export type CallConnState = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

export interface LiveKitParticipantView {
  /** LiveKit identity = base64url(device_id). Stable per device. */
  readonly identity: string;
  /** Account id (base64url) read off participant metadata, if present. */
  readonly accountId?: string;
  readonly name: string;
  readonly isLocal: boolean;
  readonly audioEnabled: boolean;
  readonly videoEnabled: boolean;
  readonly isSpeaking: boolean;
  readonly isScreenSharing: boolean;
  /** Camera track for attaching to a <video>; undefined when off/not subscribed. */
  readonly videoTrack?: Track;
  /** Screen-share track when this participant is sharing. */
  readonly screenTrack?: Track;
}

export interface LiveKitCallClientOptions {
  readonly onParticipants: (participants: LiveKitParticipantView[]) => void;
  readonly onState: (state: CallConnState) => void;
  readonly onActiveSpeakers?: (identities: string[]) => void;
  /** Raw data-channel payload from a peer (reactions / hand-raise / thread). */
  readonly onData?: (payload: Uint8Array, fromIdentity: string | undefined) => void;
  /** Fired when remote audio playback is blocked by the browser (needs gesture). */
  readonly onAudioPlaybackBlocked?: () => void;
  readonly onError?: (err: unknown) => void;
}

// Capped publish quality — 720p is plenty for tiles and keeps CPU/bandwidth
// sane in large rooms (dynacast + simulcast still down-scale per subscriber).
// Safari encodes WebRTC video noticeably more expensively (software VP8/H.264
// on many Macs), and a starved encoder lags the *whole* call — so Safari
// captures at 540p with lighter simulcast layers. dynacast still drops layers
// nobody watches; this just lowers the ceiling on the most constrained engine.
function publishVideoPreset(): VideoPreset {
  return isSafari() ? VideoPresets.h540 : VideoPresets.h720;
}
function simulcastLayers(): VideoPreset[] {
  return isSafari() ? [VideoPresets.h180] : [VideoPresets.h180, VideoPresets.h360];
}

/** Whether WebRTC Encoded Transform (required for LiveKit E2EE) is available. */
function e2eeSupported(): boolean {
  if (typeof window === 'undefined' || typeof Worker === 'undefined') return false;
  const w = window as unknown as {
    RTCRtpScriptTransform?: unknown;
    RTCRtpSender?: { prototype?: Record<string, unknown> };
  };
  const hasScriptTransform = typeof w.RTCRtpScriptTransform !== 'undefined';
  const hasInsertable =
    typeof w.RTCRtpSender?.prototype === 'object' &&
    w.RTCRtpSender?.prototype !== null &&
    'createEncodedStreams' in (w.RTCRtpSender.prototype as Record<string, unknown>);
  return hasScriptTransform || hasInsertable;
}

function createE2EEWorker(): Worker {
  // Vite resolves this to a hashed worker chunk. Module workers are supported
  // on Chromium + Safari 15+ (the same baseline as Encoded Transform).
  return new Worker(new URL('livekit-client/e2ee-worker', import.meta.url), {
    type: 'module',
  });
}

export class LiveKitCallClient {
  private readonly opts: LiveKitCallClientOptions;
  readonly keyProvider: ExternalE2EEKeyProvider;
  /** True once E2EE is active on the room; false when unsupported (fallback). */
  e2eeActive = false;
  private room: Room | null = null;
  private worker: Worker | null = null;
  /** Attached remote audio sinks, keyed by track sid, for clean teardown. */
  private readonly audioEls = new Map<string, HTMLMediaElement>();
  /** Last emitted participant signature — suppresses no-op re-renders. */
  private lastSig = '';

  constructor(opts: LiveKitCallClientOptions) {
    this.opts = opts;
    this.keyProvider = new ExternalE2EEKeyProvider();
  }

  /**
   * Connect to the room, enable E2EE (best-effort) with `mediaKey`, and publish
   * the requested local media. `keyProvider.setKey` runs BEFORE connect so the
   * first frames are encrypted under the right key.
   */
  async connect(args: {
    url: string;
    token: string;
    mediaKey: Uint8Array;
    audio: boolean;
    video: boolean;
  }): Promise<void> {
    // Feed raw bytes so the worker uses the HKDF-SHA256 path: the native binary
    // labels this "HkdfSha256" with salt="LKFrameEncryptionKey" and info=zeros[128],
    // matching this side's `createKeyMaterialFromBuffer` → `getAlgoOptions("HKDF")`.
    // Avoids PBKDF2 hash-algorithm ambiguity between native and JS implementations.
    await this.keyProvider.setKey(toKeyBytes(args.mediaKey));

    const wantE2EE = e2eeSupported();
    if (wantE2EE) {
      try {
        this.worker = createE2EEWorker();
      } catch {
        this.worker = null;
      }
    }

    const room = new Room({
      adaptiveStream: true,
      dynacast: true,
      ...(this.worker ? { e2ee: { keyProvider: this.keyProvider, worker: this.worker } } : {}),
      publishDefaults: {
        simulcast: true,
        videoSimulcastLayers: simulcastLayers(),
      },
      videoCaptureDefaults: { resolution: publishVideoPreset().resolution },
    });
    this.room = room;
    this.wireEvents(room);

    if (this.worker) {
      try {
        await room.setE2EEEnabled(true);
        this.e2eeActive = true;
      } catch (err) {
        // Encoded Transform present but setup failed — degrade gracefully.
        this.e2eeActive = false;
        this.opts.onError?.(err);
      }
    }

    await room.connect(args.url, args.token);

    // Publish after connect so tracks ride the (encrypted) path.
    if (args.audio) await room.localParticipant.setMicrophoneEnabled(true);
    if (args.video) await room.localParticipant.setCameraEnabled(true);

    // Attach any audio already subscribed during connect.
    for (const p of room.remoteParticipants.values()) {
      for (const pub of p.audioTrackPublications.values()) {
        if (pub.track) this.attachAudio(pub.track as RemoteAudioTrack);
      }
    }
    if (!room.canPlaybackAudio) this.opts.onAudioPlaybackBlocked?.();

    this.emitState();
    this.emitParticipants(true);
  }

  private wireEvents(room: Room): void {
    const refresh = () => this.emitParticipants();
    room
      .on(RoomEvent.ParticipantConnected, refresh)
      .on(RoomEvent.ParticipantDisconnected, refresh)
      .on(RoomEvent.TrackSubscribed, (track: RemoteTrack, _pub: RemoteTrackPublication) => {
        if (track.kind === Track.Kind.Audio) this.attachAudio(track as RemoteAudioTrack);
        refresh();
      })
      .on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        if (track.kind === Track.Kind.Audio && track.sid) this.detachAudio(track.sid);
        refresh();
      })
      .on(RoomEvent.LocalTrackPublished, refresh)
      .on(RoomEvent.LocalTrackUnpublished, refresh)
      .on(RoomEvent.TrackMuted, refresh)
      .on(RoomEvent.TrackUnmuted, refresh)
      .on(RoomEvent.ParticipantMetadataChanged, refresh)
      .on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) => {
        this.opts.onActiveSpeakers?.(speakers.map((s) => s.identity));
        refresh();
      })
      .on(RoomEvent.DataReceived, (payload: Uint8Array, participant?: RemoteParticipant) => {
        this.opts.onData?.(payload, participant?.identity);
      })
      .on(RoomEvent.AudioPlaybackStatusChanged, () => {
        if (this.room && !this.room.canPlaybackAudio) this.opts.onAudioPlaybackBlocked?.();
      })
      .on(RoomEvent.ConnectionStateChanged, () => this.emitState())
      .on(RoomEvent.Disconnected, () => this.emitState())
      .on(RoomEvent.Reconnecting, () => this.emitState())
      .on(RoomEvent.Reconnected, () => {
        this.emitState();
        this.emitParticipants(true);
      })
      .on(RoomEvent.MediaDevicesError, (e: Error) => this.opts.onError?.(e));
  }

  private attachAudio(track: RemoteAudioTrack): void {
    const sid = track.sid;
    if (!sid || this.audioEls.has(sid)) return;
    const el = track.attach(); // creates an autoplaying <audio>
    el.setAttribute('data-ping-call-audio', sid);
    this.audioEls.set(sid, el);
  }

  private detachAudio(sid: string): void {
    const el = this.audioEls.get(sid);
    if (el) {
      el.remove();
      this.audioEls.delete(sid);
    }
  }

  /** Resume blocked remote audio. Call from a user gesture (Safari/Chrome). */
  async startAudioPlayback(): Promise<void> {
    try {
      await this.room?.startAudio();
    } catch {
      /* still blocked; UI keeps the prompt */
    }
  }

  private emitState(): void {
    const state = this.room?.state;
    let mapped: CallConnState = 'connecting';
    switch (state) {
      case ConnectionState.Connected:
        mapped = 'connected';
        break;
      case ConnectionState.Reconnecting:
        mapped = 'reconnecting';
        break;
      case ConnectionState.Disconnected:
        mapped = 'disconnected';
        break;
      default:
        mapped = 'connecting';
    }
    this.opts.onState(mapped);
  }

  private emitParticipants(force = false): void {
    if (!this.room) return;
    const out: LiveKitParticipantView[] = [this.viewFor(this.room.localParticipant, true)];
    for (const p of this.room.remoteParticipants.values()) out.push(this.viewFor(p, false));
    // Suppress no-op emissions (LiveKit fires many events that don't change our
    // projection) so the React grid doesn't re-render needlessly. `isSpeaking`
    // is deliberately NOT in the signature: the active-speaker ring is driven by
    // the separate `onActiveSpeakers` channel, so folding it in here would re-emit
    // the entire participant array several times a second and re-render every tile
    // for nothing. The `isSpeaking` field still rides along, just doesn't trigger.
    const sig = out
      .map(
        (v) =>
          `${v.identity}:${v.audioEnabled ? 1 : 0}${v.videoEnabled ? 1 : 0}` +
          `${v.isScreenSharing ? 1 : 0}` +
          `${v.videoTrack?.sid ?? '-'}${v.screenTrack?.sid ?? '-'}`,
      )
      .join('|');
    if (!force && sig === this.lastSig) return;
    this.lastSig = sig;
    this.opts.onParticipants(out);
  }

  private viewFor(p: Participant, isLocal: boolean): LiveKitParticipantView {
    const camPub = p.getTrackPublication(Track.Source.Camera);
    const screenPub = p.getTrackPublication(Track.Source.ScreenShare);
    const micPub = p.getTrackPublication(Track.Source.Microphone);
    const accountId = readAccountId(p.metadata);
    return {
      identity: p.identity,
      ...(accountId !== undefined ? { accountId } : {}),
      name: p.name || p.identity,
      isLocal,
      audioEnabled: micPub ? !micPub.isMuted : false,
      videoEnabled: camPub ? !camPub.isMuted && camPub.track !== undefined : false,
      isSpeaking: p.isSpeaking,
      isScreenSharing: screenPub?.track !== undefined,
      ...(camPub?.track ? { videoTrack: camPub.track } : {}),
      ...(screenPub?.track ? { screenTrack: screenPub.track } : {}),
    };
  }

  async setMicEnabled(enabled: boolean): Promise<void> {
    await this.room?.localParticipant.setMicrophoneEnabled(enabled);
    this.emitParticipants(true);
  }

  async setCameraEnabled(enabled: boolean): Promise<void> {
    await this.room?.localParticipant.setCameraEnabled(enabled);
    this.emitParticipants(true);
  }

  async setScreenShareEnabled(enabled: boolean): Promise<void> {
    await this.room?.localParticipant.setScreenShareEnabled(enabled, { audio: true });
    this.emitParticipants(true);
  }

  /** Swap the published camera track's processor (background effect). */
  localCameraTrack(): LocalVideoTrack | undefined {
    const pub = this.room?.localParticipant.getTrackPublication(Track.Source.Camera);
    return pub?.track as LocalVideoTrack | undefined;
  }

  isMicEnabled(): boolean {
    return this.room?.localParticipant.isMicrophoneEnabled ?? false;
  }

  isCameraEnabled(): boolean {
    return this.room?.localParticipant.isCameraEnabled ?? false;
  }

  isScreenSharing(): boolean {
    return this.room?.localParticipant.isScreenShareEnabled ?? false;
  }

  /** Switch the active input/output device (mic / camera / speaker). */
  async switchDevice(kind: MediaDeviceKind, deviceId: string): Promise<void> {
    await this.room?.switchActiveDevice(kind, deviceId);
  }

  /**
   * Locally set a remote participant's audio volume (0–1). This is a *local*
   * mute — it only silences the speaker for the current user, it does NOT mute
   * them for everyone (that needs server-side moderator permissions). Applies to
   * the participant's audible track on this client only. Idempotent + safe if
   * the participant has left.
   */
  setParticipantVolume(identity: string, volume: number): void {
    const room = this.room;
    if (!room) return;
    for (const p of room.remoteParticipants.values()) {
      if (p.identity === identity) {
        p.setVolume(volume);
        return;
      }
    }
  }

  /** Send opaque data to all peers (reactions / hand-raise / thread / notes). */
  async publishData(payload: Uint8Array, reliable = true): Promise<void> {
    await this.room?.localParticipant.publishData(payload, { reliable });
  }

  async disconnect(): Promise<void> {
    const room = this.room;
    this.room = null;
    for (const el of this.audioEls.values()) el.remove();
    this.audioEls.clear();
    try {
      room?.removeAllListeners();
      // Explicitly stop every local media track so the camera/mic hardware is
      // released immediately (camera indicator light off). `disconnect(true)`
      // also stops tracks, but a background processor can wrap the camera in a
      // processed track while keeping the original device track open — stopping
      // both the publication track and its underlying MediaStreamTracks here is
      // the belt-and-braces guard against that leak.
      const lp = room?.localParticipant;
      if (lp) {
        for (const pub of lp.trackPublications.values()) {
          const track = pub.track;
          if (!track) continue;
          try {
            track.stop();
            track.mediaStreamTrack?.stop();
          } catch {
            /* track already stopped */
          }
        }
      }
      await room?.disconnect(true);
    } finally {
      this.worker?.terminate();
      this.worker = null;
    }
  }
}

/** Re-exports so callers can type attach helpers. */
export type { LocalVideoTrack, RemoteParticipant,TrackPublication };

function readAccountId(metadata: string | undefined): string | undefined {
  if (!metadata) return undefined;
  try {
    const m = JSON.parse(metadata) as { account_id?: unknown };
    return typeof m.account_id === 'string' ? m.account_id : undefined;
  } catch {
    return undefined;
  }
}
